import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, FileWarning, CalendarDays, PlayCircle, Camera, HandHeart, Mail, Search, Newspaper, Users, Vote, Landmark, PiggyBank, MapPin, ExternalLink, ShieldCheck, ListChecks, CalendarClock, Signpost, HeartHandshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { api, fileUrl } from '../lib/api';
import { Rich, StatusBadge, SubscribeForm, formatDate, overlayStyle, ISSUE_CATEGORIES } from '../components/Shared';
import { MoreDetails } from '../components/MoreDetails';
import { toYouTubeEmbed } from '../lib/youtube';
import { IntroVideoModal, INTRO_DEFAULTS, hasSeenIntro } from '../components/IntroVideo';
import { HopeBadge, HopeTaglineStrip, HopeHomepageSection } from '../components/HopeTheme';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { CvDownloadSection } from '../components/CvDownload';
import { usePitaLogo } from '../components/PitaCTA';
import { ReviewsHomeSection } from '../components/ReviewBlocks';
import { Ward8PhotoStrip } from '../components/Ward8PhotoStrip';
import { usePageContent } from '../lib/pageContent';
import { useNavigate } from 'react-router-dom';

// Site structure v3 (Phase 123): concise section links directly under the
// hero so the homepage doubles as a clear map of the site. Mirrors the main
// menu order from the site structure document.
const HOME_SECTION_LINKS = [
  { label: 'Request a Sign', body: 'Free lawn sign, placed by a volunteer.', url: '/request-a-sign', icon: Signpost, testid: 'home-quick-request-sign' },
  { label: 'Election Event List', body: 'Debates, forums and key dates.', url: '/elections/events', icon: CalendarDays, testid: 'home-quick-events' },
  { label: 'PITA', body: 'Report It. Map It. Track It. Get Answers.', url: '/issue-tracker', icon: MapPin, testid: 'home-quick-pita', accent: true },
  { label: 'Media', body: "Steve's videos, posters and photos.", url: '/media', icon: PlayCircle, testid: 'home-quick-media' },
  { label: 'About Steve', body: 'Platform, CV and flyer, reviews, why he is running.', url: '/about', icon: Users, testid: 'home-quick-about' },
  { label: 'Resources', body: "Who's running, your ward, City data, ways to help.", url: '/resources', icon: Landmark, testid: 'home-quick-resources' },
  { label: 'Contact', body: 'Questions, suggestions, a sign request.', url: '/contact', icon: Mail, testid: 'home-quick-contact' },
  { label: 'Donate', body: 'How contributions work under Ontario rules.', url: '/donate', icon: HeartHandshake, testid: 'home-quick-donate', gold: true },
];

const HomeSectionLinks = () => (
  <section className="bg-white border-b border-slate-200 py-6 md:py-8" aria-label="Explore the site" data-testid="home-section-links">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs font-bold uppercase tracking-widest text-[#24406f] mb-3">Explore the site</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
        {HOME_SECTION_LINKS.map(({ icon: Icon, ...l }) => (
          <Link key={l.url} to={l.url} data-testid={l.testid}
            className={`group flex items-start gap-3 rounded-xl border p-3 sm:p-4 min-h-[64px] transition-colors duration-150 ${l.accent ? 'border-[#b91c1c]/40 bg-red-50/60 hover:bg-red-50' : l.gold ? 'border-[#C9A84C]/60 bg-[#FBF7EE] hover:bg-[#f6efdd]' : 'border-slate-200 bg-[#F6F8FB] hover:border-[#1a2744]/40 hover:bg-[#eaf2ff]'}`}>
            <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${l.accent ? 'bg-[#b91c1c]' : l.gold ? 'bg-[#C9A84C]' : 'bg-[#1a2744]'}`}><Icon className={`h-4 w-4 ${l.gold ? 'text-[#1a2744]' : 'text-white'}`} /></span>
            <span className="min-w-0">
              <span className="block text-sm font-black text-[#1a2744] leading-tight">{l.label}</span>
              <span className="hidden sm:block text-xs text-slate-600 mt-0.5 leading-snug">{l.body}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// PITA logo card for the homepage PITA hero/promo block (respects admin placement toggle).
const HomePitaLogo = () => {
  const pitaLogo = usePitaLogo('home_hero');
  if (!pitaLogo) return null;
  return (
    <div className="bg-white rounded-xl p-2 shrink-0 h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center" data-testid="home-pita-logo-card">
      <img src={pitaLogo.url} alt={pitaLogo.alt} className="max-h-full max-w-full object-contain" data-testid="home-pita-logo" />
    </div>
  );
};

const CTA_STYLES = {
  // 'gold' is the admin's "primary action" style name: rendered RED under the
  // Phase 92/93 palette (red = TAKE ACTION)
  gold: 'bg-[#b91c1c] hover:bg-[#991b1b] text-white',
  navy: 'bg-[#1a2744] hover:bg-[#243456] text-white border border-white/30',
  outline: 'bg-transparent hover:bg-white/10 text-white border-2 border-white',
};

// CTA styles adapted for the bright "light_logo" hero variant (dark navy text on light bg)
const LIGHT_CTA_STYLES = {
  // 'gold' = primary action style name: rendered RED on light slides (red = TAKE ACTION)
  gold: 'bg-[#b91c1c] hover:bg-[#991b1b] text-white',
  navy: 'bg-[#1a2744] hover:bg-[#243456] text-white',
  outline: 'bg-transparent hover:bg-[#1a2744]/5 text-[#1a2744] border-2 border-[#1a2744]',
};

// Full-background bright hero: Steve photo fills the slide (right-adjusted),
// corrected logo + date pill + copy on the left over a light readability gradient.
const LOGO_SIZE_CLASSES = {
  sm: 'max-w-[220px] sm:max-w-xs',
  md: 'max-w-xs sm:max-w-md',
  lg: 'max-w-sm sm:max-w-lg lg:max-w-xl',
};

const LightLogoSlide = ({ slide, hope }) => {
  const strength = typeof slide.gradient_strength === 'number' ? slide.gradient_strength : 85;
  const a = Math.max(0, Math.min(100, strength)) / 100;
  const gradient = `linear-gradient(to right, rgba(255,253,248,${a.toFixed(2)}) 0%, rgba(255,253,248,${(a * 0.9).toFixed(2)}) 38%, rgba(255,253,248,${(a * 0.4).toFixed(2)}) 62%, rgba(255,253,248,0) 84%)`;
  const textColor = slide.text_color && slide.text_color.toLowerCase() !== '#ffffff' ? slide.text_color : '#1a2744';
  return (
    <>
      {/* Full-bleed background photo — Steve right-adjusted, no card/box */}
      <img src={fileUrl(slide.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover sm:hidden" style={{ objectPosition: slide.focus_mobile || '76% center' }} data-testid="hero-steve-photo-mobile" />
      <img src={fileUrl(slide.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover hidden sm:block" style={{ objectPosition: slide.focus_desktop || 'right center' }} data-testid="hero-steve-photo" />
      {/* Light readability gradient (left → transparent right); extra soft veil on mobile only */}
      <div className="absolute inset-0" style={{ background: gradient }} />
      <div className="absolute inset-0 bg-white/40 sm:bg-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24 w-full" data-testid="hero-light-logo-slide">
        <div className="max-w-2xl">
          {hope?.hero_badge?.show !== false && hope?.hero_badge?.text && (
            <HopeBadge text={hope.hero_badge.text} className="mb-4 !text-[#8a6f22] !border-[#C9A84C] !bg-[#C9A84C]/15" />
          )}
          {slide.date_text && (
            <div className="mb-4 lg:mb-5">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/95 border border-[#1a2744]/15 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-[#1a2744] shadow-sm" data-testid="hero-date-text">
                <Vote className="h-4 w-4 text-[#8a6f22]" /> {slide.date_text}
              </p>
            </div>
          )}
          <h1 className="sr-only">{slide.title || 'Elect Steve Lesperance for CKL Councillor Ward 8'}</h1>
          {slide.logo_url && (
            <img src={fileUrl(slide.logo_url)} alt={slide.title || 'Elect Steve Lesperance for CKL Councillor Ward 8, SteveLesperance.com'} className={`w-full h-auto ${LOGO_SIZE_CLASSES[slide.logo_size] || LOGO_SIZE_CLASSES.lg}`} data-testid="hero-logo-image" />
          )}
          {slide.subtitle && <p className="mt-5 text-base sm:text-xl font-bold leading-snug max-w-xl" style={{ color: textColor }} data-testid="hero-light-subtitle">{slide.subtitle}</p>}
          {slide.description && <p className="mt-2.5 text-sm sm:text-base font-medium text-slate-700 leading-relaxed max-w-xl" data-testid="hero-light-description">{slide.description}</p>}
          {(slide.ctas || []).length > 0 && (
            <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
              {(slide.ctas || []).map((cta, i) => (
                <Button key={i} asChild className={`h-12 px-5 font-bold btn-press w-full sm:w-auto justify-center ${LIGHT_CTA_STYLES[cta.style] || LIGHT_CTA_STYLES.gold}`} data-testid={i === 0 ? 'home-hero-primary-cta' : `home-hero-cta-${i}`}>
                  <Link to={cta.url || '/'}>{cta.label}</Link>
                </Button>
              ))}
            </div>
          )}
          {(slide.secondary_ctas || []).length > 0 && (
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
              {slide.secondary_ctas.map((c, i) => (
                <Link key={i} to={c.url || '/'} className="inline-flex items-center gap-1 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4 hover:text-[#243456]" data-testid={`hero-secondary-link-${i}`}>
                  {c.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Power-punch civic slide for the Ward 8 Public Issue Tracker App (PITA) (variant: tracker_promo)
// Dark navy campaign-tech look: subtle map-grid background, logo showcase, chips, CTAs.
const TrackerPromoSlide = ({ slide }) => {
  const chips = (slide.chips || '').split('\n').map((c) => c.trim()).filter(Boolean);
  const bodyParas = (slide.body || '').split('\n\n').map((p) => p.trim()).filter(Boolean);
  const hasPhoto = !!slide.image_url;
  // Navy wash over the aerial photo — colour + strength are admin-editable (Hero Manager overlay controls)
  const washColor = slide.overlay?.color || '#141f36';
  const washOpacity = Math.min(Math.max((typeof slide.overlay?.opacity === 'number' ? slide.overlay.opacity : 78) / 100, 0), 1);
  return (
    <div className="relative h-full flex flex-col justify-start" data-testid="hero-tracker-promo-slide">
      {/* Background: aerial photo + admin-controlled navy wash, else layered civic map-grid pattern */}
      {hasPhoto ? (
        <>
          <img src={fileUrl(slide.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover sm:hidden" style={{ objectPosition: slide.focus_mobile || 'center' }} data-testid="tracker-hero-photo-mobile" />
          <img src={fileUrl(slide.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover hidden sm:block" style={{ objectPosition: slide.focus_desktop || 'center' }} data-testid="tracker-hero-photo" />
          <div className="absolute inset-0" style={{ backgroundColor: washColor, opacity: washOpacity }} data-testid="tracker-hero-wash" />
          {/* Readability gradients: lighter vertical on mobile (lets the aerial show through), left-side on larger screens */}
          <div aria-hidden="true" className="absolute inset-0 sm:hidden bg-gradient-to-b from-[#0d1526]/55 via-[#141f36]/25 to-[#0d1526]/65" />
          <div aria-hidden="true" className="absolute inset-0 hidden sm:block bg-gradient-to-r from-[#0d1526]/85 via-[#141f36]/40 to-transparent" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#141f36] via-[#1a2744] to-[#22345c]" />
      )}
      {/* Subtle municipal grid + route lines + pin dots */}
      <div aria-hidden="true" className={`absolute inset-0 ${hasPhoto ? 'opacity-[0.07]' : 'opacity-[0.13]'}`} style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px)', backgroundSize: '56px 56px' }} />
      <svg aria-hidden="true" className={`absolute inset-0 w-full h-full ${hasPhoto ? 'opacity-[0.10]' : 'opacity-[0.16]'}`} preserveAspectRatio="none" viewBox="0 0 1200 600">
        <path d="M-20 480 L260 430 L430 470 L640 380 L900 420 L1220 330" fill="none" stroke="#C9A84C" strokeWidth="2" strokeDasharray="10 8" />
        <path d="M-20 200 L180 250 L420 190 L700 260 L980 180 L1220 230" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="4 8" />
        <circle cx="260" cy="430" r="5" fill="#C9A84C" /><circle cx="640" cy="380" r="5" fill="#C9A84C" /><circle cx="980" cy="180" r="4" fill="#ffffff" /><circle cx="420" cy="190" r="4" fill="#ffffff" />
      </svg>
      <div className="absolute inset-0 noise-overlay" />

      {/* Consistent slide grid: same top offset + max width on every slide
          so the H1 never jumps up or down between slides (Phase 93) */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 lg:pt-20 pb-14 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: message + CTAs */}
          <div className="lg:col-span-7 min-w-0">
            {slide.kicker && (
              <p className="inline-flex items-center gap-2 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/50 px-3.5 py-1.5 text-[11px] sm:text-xs font-black uppercase tracking-widest text-[#e8c96a]" data-testid="tracker-hero-kicker">{slide.kicker}</p>
            )}
            {/* Mobile logo — compact, above the title */}
            {slide.logo_url && (
              <img src={fileUrl(slide.logo_url)} alt={slide.title} className="lg:hidden h-16 w-16 rounded-2xl shadow-lg ring-1 ring-white/20 mt-4" data-testid="tracker-hero-logo-mobile" />
            )}
            <h1 className="mt-4 hero-h1 text-3xl sm:text-5xl text-white hero-text-shadow whitespace-pre-line" data-testid="tracker-hero-title">{slide.title}</h1>
            {slide.subtitle && <p className="mt-3 text-lg sm:text-2xl font-bold text-[#C9A84C] hero-text-shadow" data-testid="tracker-hero-tagline">{slide.subtitle}</p>}
            {slide.description && (
              <p className="mt-4 border-l-4 border-[#C9A84C] pl-3.5 font-serif text-base sm:text-xl font-bold text-white leading-snug max-w-2xl" data-testid="tracker-hero-punch">{slide.description}</p>
            )}
            {bodyParas.length > 0 && (
              <div className="mt-4 space-y-2.5 max-w-2xl" data-testid="tracker-hero-body">
                {bodyParas.map((p, i) => (
                  <p key={i} className={`text-sm sm:text-base text-slate-200 leading-relaxed ${i > 0 ? 'hidden sm:block' : ''}`}>{p}</p>
                ))}
              </div>
            )}
            {chips.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2" data-testid="tracker-hero-chips">
                {chips.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[11px] sm:text-xs font-bold text-white">
                    <MapPin className="h-3 w-3 text-[#C9A84C]" /> {c}
                  </span>
                ))}
              </div>
            )}
            {slide.ctas?.length > 0 && (
              <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
                {(slide.ctas || []).map((cta, i) => (
                  <Button key={i} asChild className={`h-12 px-5 font-bold btn-press w-full sm:w-auto justify-center ${CTA_STYLES[cta.style] || CTA_STYLES.gold}`} data-testid={i === 0 ? 'tracker-hero-primary-cta' : `tracker-hero-cta-${i}`}>
                    <Link to={cta.url || '/'}>{cta.label}</Link>
                  </Button>
                ))}
              </div>
            )}
            {slide.secondary_ctas?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                {(slide.secondary_ctas || []).map((c, i) => (
                  <Link key={i} to={c.url || '/'} className="inline-flex items-center gap-1 text-sm font-bold text-white/90 underline decoration-[#C9A84C] decoration-2 underline-offset-4 hover:text-white" data-testid={`tracker-hero-secondary-${i}`}>
                    {c.label} <ArrowRight className="h-3.5 w-3.5 text-[#C9A84C]" />
                  </Link>
                ))}
              </div>
            )}
            {slide.callout && (
              <p className="mt-5 font-serif font-bold text-sm sm:text-base text-[#e8c96a]" data-testid="tracker-hero-callout">{slide.callout}</p>
            )}
          </div>

          {/* Right: logo showcase + value card (desktop) */}
          <div className="lg:col-span-5 min-w-0">
            {slide.logo_url && (
              <div className="hidden lg:flex justify-center" data-testid="tracker-hero-logo-desktop">
                <div className="relative">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-[2.5rem] bg-[#C9A84C]/20 blur-2xl" />
                  <img src={fileUrl(slide.logo_url)} alt={slide.title} className="relative w-56 xl:w-64 rounded-3xl shadow-2xl ring-1 ring-white/25" />
                </div>
              </div>
            )}
            {(slide.value_heading || slide.value_text || slide.caution_text) && (
              <div className="mt-6 lg:mt-8 rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-sm p-4 sm:p-5" data-testid="tracker-hero-value-card">
                {slide.value_heading && <p className="font-serif font-bold text-[#e8c96a] text-sm sm:text-base leading-snug">{slide.value_heading}</p>}
                {slide.value_text && <p className="mt-2 text-xs sm:text-sm text-slate-200 leading-relaxed">{slide.value_text}</p>}
                {slide.caution_text && <p className="mt-2.5 pt-2.5 border-t border-white/10 text-[11px] sm:text-xs text-slate-400 leading-relaxed" data-testid="tracker-hero-caution">{slide.caution_text}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Innovation / scale strip */}
        {slide.strip_text && (
          <div className="mt-8 pt-4 border-t border-white/15" data-testid="tracker-hero-strip">
            <p className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-white/80">{slide.strip_text}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Kawartha Lakes 2026 Election Centre promo slide (variant: election_promo).
// Mirrors the approved /elections hero: navy civic grid, gold accents, plain wording,
// confirm-with-the-City note. Buttons with http(s) links open in a new tab.
const ElectionPromoSlide = ({ slide }) => {
  const external = (url) => /^https?:\/\//i.test(url || '');
  const hasBg = !!slide.image_url;
  return (
    <div className="relative h-full flex flex-col justify-start" data-testid="hero-election-promo-slide">
      {hasBg ? (
        <>
          {/* Civic voting background (admin-editable image). Right-weighted art keeps the left text column clean. */}
          <img src={fileUrl(slide.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: slide.focus_desktop || '78% center' }} data-testid="election-slide-bg" />
          {/* Soft left readability gradient only; the artwork stays visible on the right */}
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-[#0d1526]/80 via-[#141f36]/45 to-transparent" />
          <div aria-hidden="true" className="absolute inset-0 sm:hidden bg-[#0d1526]/35" />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-[#152038]" />
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.06]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
        </>
      )}
      <div aria-hidden="true" className="absolute top-0 inset-x-0 h-1 bg-[#C9A84C]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 lg:pt-20 pb-14 w-full">
        {slide.kicker && <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#e8c96a]" data-testid="election-slide-eyebrow">{slide.kicker}</p>}
        <h1 className="mt-2 hero-h1 text-3xl sm:text-5xl text-white whitespace-pre-line max-w-3xl" data-testid="election-slide-headline">{slide.title}</h1>
        {slide.subtitle && <p className="mt-3 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-2xl" data-testid="election-slide-subheadline">{slide.subtitle}</p>}
        {slide.description && <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl hidden sm:block" data-testid="election-slide-body">{slide.description}</p>}
        {(slide.ctas || []).length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="election-slide-buttons">
            {slide.ctas.map((cta, i) => {
              const cls = i === 0
                ? 'h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press'
                : 'h-12 px-5 bg-transparent hover:bg-white/10 text-white border-2 border-white/70 font-bold btn-press';
              return external(cta.url) ? (
                <Button key={i} asChild className={cls} data-testid={`election-slide-cta-${i}`}>
                  <a href={cta.url} target="_blank" rel="noopener noreferrer">{cta.label} <ExternalLink className="h-4 w-4 ml-1.5" /></a>
                </Button>
              ) : (
                <Button key={i} asChild className={cls} data-testid={`election-slide-cta-${i}`}>
                  <Link to={cta.url || '/elections'}>{cta.label}</Link>
                </Button>
              );
            })}
          </div>
        )}
        {slide.note && (
          <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-white/10 border border-[#C9A84C]/40 p-3.5 max-w-2xl" data-testid="election-slide-note">
            <ShieldCheck className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-200 leading-relaxed">{slide.note}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Renders one slide's content by variant (used by the stacked equal-height carousel).
const SlideBody = ({ slide, hope, extraCta }) => {
  if (slide.variant === 'light_logo') return <LightLogoSlide slide={slide} hope={hope} />;
  if (slide.variant === 'tracker_promo') return <TrackerPromoSlide slide={slide} />;
  if (slide.variant === 'election_promo') return <ElectionPromoSlide slide={slide} />;
  const align = slide.text_align === 'center' ? 'items-center text-center' : slide.text_align === 'right' ? 'items-end text-right' : 'items-start text-left';
  return (
    <>
      {slide.media_type === 'video' && slide.video_url ? (
        <video src={fileUrl(slide.video_url)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
      ) : slide.media_type === 'embed' && slide.embed_url ? (
        <iframe src={slide.embed_url} title="Hero video" className="absolute inset-0 w-full h-full object-cover pointer-events-none" allow="autoplay" />
      ) : (
        <img src={fileUrl(slide.image_url)} alt="" className={`absolute inset-0 w-full h-full object-cover ${slide.overlay?.blur ? 'blur-sm scale-105' : ''}`} style={{ objectPosition: slide.overlay?.focus_position || 'center' }} />
      )}
      <div className="absolute inset-0" style={overlayStyle(slide.overlay)} />
      <div className="absolute inset-0 noise-overlay" />

      <div className={`relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 lg:pt-20 pb-14 flex flex-col w-full ${align}`}>
        {hope?.hero_badge?.show !== false && hope?.hero_badge?.text && (
          <HopeBadge text={hope.hero_badge.text} className="mb-4" />
        )}
        <h1 className={`hero-h1 text-3xl sm:text-5xl whitespace-pre-line max-w-3xl ${slide.overlay?.text_shadow !== false ? 'hero-text-shadow' : ''}`} style={{ color: slide.text_color || '#fff' }}>
          {slide.title}
        </h1>
        {slide.subtitle && (
          <p className={`mt-3 text-lg sm:text-2xl font-bold text-[#C9A84C] ${slide.overlay?.text_shadow !== false ? 'hero-text-shadow' : ''}`}>{slide.subtitle}</p>
        )}
        {hope?.hero_subtitle?.show && hope?.hero_subtitle?.text && (
          <p className="mt-2 text-sm sm:text-lg font-semibold text-slate-100 hero-text-shadow" data-testid="hope-hero-subtitle">{hope.hero_subtitle.text}</p>
        )}
        {slide.description && (
          <p className="mt-4 text-sm sm:text-lg text-slate-100 leading-relaxed max-w-2xl hero-text-shadow">{slide.description}</p>
        )}
        {(slide.ctas?.length > 0 || extraCta) && (
          <div className="mt-7 flex flex-wrap gap-3">
            {(slide.ctas || []).map((cta, i) => (
              <Button key={i} asChild className={`h-12 px-5 font-bold btn-press ${CTA_STYLES[cta.style] || CTA_STYLES.gold}`} data-testid={i === 0 ? 'home-hero-primary-cta' : `home-hero-cta-${i}`}>
                <Link to={cta.url || '/'}>{cta.label}</Link>
              </Button>
            ))}
            {extraCta && (
              <Button onClick={extraCta.onClick} className={`h-12 px-5 font-bold btn-press ${CTA_STYLES.gold}`} data-testid="hero-steve-message-cta">
                {extraCta.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// Rotation timing: slower on purpose so seniors and mobile readers can finish each slide.
const HERO_ROTATION_MS = 9000;

const HeroCarousel = ({ slides, extraCta, hope }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchRef = useRef({ x: 0, y: 0, active: false });
  const next = useCallback(() => setIdx((i) => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    // Recreated on every slide change so manual arrows/dots restart the full
    // reading time. Pauses while the mouse is over the hero so slow readers
    // never lose their place.
    const t = setInterval(next, HERO_ROTATION_MS);
    return () => clearInterval(t);
  }, [next, slides.length, idx, paused]);

  // Touch swipe (mobile photo-gallery feel): swipe left/right to change
  // slides. Vertical scrolling is untouched — we only react to clearly
  // horizontal swipes. Autoplay pauses during the touch and resumes after.
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
    setPaused(true);
  }, []);
  const onTouchEnd = useCallback((e) => {
    const start = touchRef.current;
    touchRef.current = { ...start, active: false };
    setPaused(false);
    if (!start.active || slides.length < 2) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next(); else prev();
    }
  }, [next, prev, slides.length]);
  const onTouchCancel = useCallback(() => {
    touchRef.current.active = false;
    setPaused(false);
  }, []);

  if (!slides.length) return null;
  const slide = slides[idx];
  const isLight = slide.variant === 'light_logo';

  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
    >
      {/* All slides share one grid cell so the hero keeps ONE consistent height
          across every slide and breakpoint (no jumping, no layout shift). */}
      <div className="grid">
        {slides.map((s, i) => (
          <div
            key={s.id || i}
            aria-hidden={i !== idx}
            className={`col-start-1 row-start-1 relative flex flex-col justify-center transition-opacity duration-700 ${i === idx ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'} ${s.variant === 'light_logo' ? 'bg-gradient-to-b from-[#e9f1fb] via-[#f6faff] to-white' : 'bg-[#1a2744]'}`}
            data-testid={`home-hero-slide-${i}`}
          >
            <SlideBody slide={s} hope={hope} extraCta={extraCta} />
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={prev} data-testid="home-hero-prev" aria-label="Previous slide" className={`absolute z-20 left-2 top-1/2 -translate-y-1/2 p-2 rounded-full hidden sm:block ${isLight ? 'bg-[#1a2744]/15 text-[#1a2744] hover:bg-[#1a2744]/25' : 'bg-black/30 text-white hover:bg-black/50'}`}>
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={next} data-testid="home-hero-next" aria-label="Next slide" className={`absolute z-20 right-2 top-1/2 -translate-y-1/2 p-2 rounded-full hidden sm:block ${isLight ? 'bg-[#1a2744]/15 text-[#1a2744] hover:bg-[#1a2744]/25' : 'bg-black/30 text-white hover:bg-black/50'}`}>
            <ChevronRight className="h-6 w-6" />
          </button>
          <div className="absolute z-20 bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} aria-current={i === idx} className="p-2.5 -m-1 rounded-full flex items-center" data-testid={`hero-slide-dot-${i + 1}`}>
                <span aria-hidden="true" className={`block h-2.5 rounded-full transition-colors duration-150 ${i === idx ? 'w-8 bg-[#C9A84C]' : `w-2.5 ${isLight ? 'bg-[#1a2744]/25' : 'bg-white/50'}`}`} />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

// Six accountability pillars: explains the entire website in seconds.
// Simple first (icon, title, one sentence, button); depth lives behind More...
const ACCOUNTABILITY_PILLARS = [
  { id: 'your-vote', icon: Vote, title: 'YOUR VOTE', body: 'Elections the people can see and trust.', label: 'Paper Ballots', url: '/paper-ballots',
    more: 'Steve wants the paper ballot choice restored before the 2026 municipal election. Witness Your Vote is his independent transparency initiative for the vote itself.' },
  { id: 'your-issues', icon: FileWarning, title: 'YOUR ISSUES', body: 'Report a City problem and follow it to an answer.', label: 'Open PITA', url: '/issue-tracker',
    more: (<>Every <Link to="/issue-tracker" className="pita-link" data-testid="pita-static-link-home">PITA</Link> report gets a W8 tracking number and a public status. One complaint can be overlooked. A visible pattern cannot.</>) },
  { id: 'your-money', icon: PiggyBank, title: 'YOUR MONEY', body: 'See where your tax dollars go.', label: 'See Your Money', url: '/resources#your-money',
    more: 'City Data, the Sunshine List, and the Savings Tracker: verified figures with sources, and a savings pipeline that only counts real results.' },
  { id: 'your-council', icon: Landmark, title: 'YOUR COUNCIL', body: 'Council meetings and reports, in plain language.', label: 'See Your Council', url: '/resources#your-council',
    more: 'Follow meetings, decisions, and accountability reports without wading through jargon.' },
  { id: 'your-community', icon: Users, title: 'YOUR COMMUNITY', body: 'The Ward 8 hub for local issues and updates.', label: 'Visit Ward 8', url: '/ward-8',
    more: 'Local issues on a public map, community updates, and one place for Ward 8 news.' },
  { id: 'your-election', icon: Search, title: 'YOUR ELECTION', body: 'Key dates, voting info, and who is running.', label: 'Election Centre', url: '/elections',
    more: 'The 2026 Election Centre keeps key dates, voting information, and the official candidate list current from City sources.' },
];

const AccountabilityPillarsSection = ({ whySection, whyCards = [], pillars = [], watchVideo = null }) => (
  <section className="bg-white border-b border-slate-200 py-12 md:py-16" data-testid="home-pillars-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-left max-w-3xl">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a2744] tracking-tight leading-tight" data-testid="home-pillars-headline">ACCOUNTABILITY YOU CAN SEE.</h2>
        <p className="mt-3 text-base sm:text-lg font-bold text-[#24406f]" data-testid="home-pillars-subheadline">I PROMISE ACCOUNTABILITY BY BUILDING THE SYSTEM FOR IT.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-10">
        {ACCOUNTABILITY_PILLARS.map(({ id, icon: Icon, title, body, label, url, more }) => (
          <Card key={id} className="card-lift border-slate-200" data-testid={`home-pillar-${id}`}>
            <CardContent className="p-5 flex flex-col h-full">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <p className="font-black text-[#1a2744] text-sm tracking-wide">{title}</p>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
              <div className="mt-2 flex-1">
                <MoreDetails testId={`home-pillar-more-${id}`}>
                  <p className="text-sm text-slate-600 leading-relaxed">{more}</p>
                </MoreDetails>
              </div>
              <Button asChild className="mt-4 h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press w-full" data-testid={`home-pillar-button-${id}`}>
                <Link to={url}>{label} <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Deeper story: why Steve, meet Steve, and the full platform (simple first) */}
      <div className="mt-8">
        <MoreDetails testId="home-accountability-more">
          <div className="space-y-8">
            {(whySection?.body_html || whyCards.length > 0) && (
              <div>
                <p className="font-serif font-black text-[#1a2744] text-lg mb-3">{whySection?.title || 'Steve promises accountability by building the system for it.'}</p>
                {whySection?.body_html && <Rich html={whySection.body_html} className="max-w-3xl" />}
                {whyCards.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    {whyCards.slice(0, 8).map((card) => (
                      <div key={card.id} className="bg-[#f3f7ff] border border-slate-200 rounded-xl p-4" data-testid="home-why-vote-card">
                        <p className="font-bold text-[#1a2744] text-sm leading-snug">{card.title}</p>
                        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{card.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="home-meet-steve-button"><Link to="/meet-steve">Meet Steve <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press"><Link to="/why-im-running">Why I'm Running</Link></Button>
              {watchVideo && (
                <Button onClick={watchVideo.onClick} variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="home-watch-steve-message-button">
                  <PlayCircle className="h-4 w-4 mr-1.5" /> {watchVideo.label}
                </Button>
              )}
              <Link to="/meet-steve/experience" className="text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-experience-link">See full experience</Link>
            </div>
            {pillars.length > 0 && (
              <div>
                <p className="font-serif font-black text-[#1a2744] text-lg mb-3">Ten practical commitments</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pillars.slice(0, 6).map((p) => (
                    <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid="home-platform-pillar-card">
                      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#1a2744] text-white font-black text-xs mb-2">{p.number}</span>
                      <p className="font-bold text-[#1a2744] text-sm leading-snug">{p.title}</p>
                    </div>
                  ))}
                </div>
                <Button asChild className="mt-4 h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="home-platform-button"><Link to="/platform">See the Full Platform <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              </div>
            )}
          </div>
        </MoreDetails>
      </div>
    </div>
  </section>
);

const MiniTrack = () => {
  const [num, setNum] = useState('');
  const navigate = useNavigate();
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (num.trim()) navigate(`/issue-tracker?track=${encodeURIComponent(num.trim())}`); }}
      className="flex gap-2 mt-4"
      data-testid="home-track-form"
    >
      <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Enter W8 tracking number (e.g. W8-2026-0001)" className="h-12 bg-white font-mono" data-testid="home-track-input" />
      <Button type="submit" className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white btn-press" data-testid="home-track-submit"><Search className="h-4 w-4 mr-1" /> Track</Button>
    </form>
  );
};
// ---------------------------------------------------------------------------
// HOMEPAGE SECTION TRIM (approved): SIMPLE FIRST. DETAILS WHEN YOU WANT THEM.
// Exact order below the hero:
// 1. Accountability You Can See (six cards)  2. PITA  3. Paper Ballots
// 4. Lower Taxes  5. Ward 8  6. Election Centre  7. Get Involved
// Deeper material lives behind More... or on the destination pages.
// ---------------------------------------------------------------------------

// 2) PITA — strong and short. Black impact section; red = take action.
const PitaHomeSection = () => (
  <section className="relative bg-[#0b0f17] py-14 md:py-20 overflow-hidden" data-testid="home-pita-section">
    <div aria-hidden="true" className="absolute inset-0 noise-overlay opacity-50" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-start gap-4 sm:gap-5">
        <HomePitaLogo />
        <h2 className="hero-h1 text-3xl sm:text-5xl text-white leading-tight" data-testid="home-pita-headline">
          GOT AN ISSUE?<br />PUT IT IN PITA!
        </h2>
      </div>
      <p className="mt-5 text-lg sm:text-xl font-bold text-[#C9A84C]" data-testid="home-pita-tagline">Report It. Map It. Track It. Get Answers.</p>
      <p className="mt-2 text-slate-300 leading-relaxed max-w-xl" data-testid="home-pita-pattern-line">One complaint can be overlooked. A visible pattern cannot.</p>
      <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
        <Button asChild className="h-12 px-6 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-black tracking-wide btn-press w-full sm:w-auto justify-center" data-testid="home-report-issue-button">
          <Link to="/issue-tracker/report"><FileWarning className="h-4 w-4 mr-2" /> REPORT AN ISSUE</Link>
        </Button>
        <Button asChild variant="outline" className="h-12 px-6 bg-transparent border-2 border-white/80 text-white hover:bg-white hover:text-[#0b0f17] font-bold btn-press w-full sm:w-auto justify-center" data-testid="home-see-reports-button">
          <Link to="/issue-tracker">SEE WHAT RESIDENTS ARE REPORTING</Link>
        </Button>
      </div>
      <div className="mt-6 max-w-xl">
        <MoreDetails variant="dark" testId="home-pita-more">
          <p className="text-sm text-slate-300 mb-1">Already reported something? Track it with your W8 number.</p>
          <MiniTrack />
        </MoreDetails>
      </div>
    </div>
  </section>
);

// 3) Paper Ballots — homepage preview only. Research stays on /paper-ballots.
const PaperBallotsHomeSection = () => (
  <section className="bg-white border-b border-slate-200 py-12 md:py-16" data-testid="home-paper-ballots-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a2744] tracking-tight leading-tight" data-testid="home-paper-ballots-headline">RESTORE THE PAPER BALLOT CHOICE</h2>
        <p className="mt-3 text-base sm:text-lg font-bold text-[#24406f]" data-testid="home-paper-ballots-subheadline">Before the 2026 Municipal Election</p>
        <p className="mt-3 text-slate-600 leading-relaxed" data-testid="home-paper-ballots-sentence">Steve is calling on Kawartha Lakes to give every voter a paper ballot option they can mark, see, and trust.</p>
      </div>
      <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
        <Button asChild className="h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press w-full sm:w-auto justify-center" data-testid="home-paper-ballots-why-button">
          <Link to="/paper-ballots">WHY PAPER BALLOTS? <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
        </Button>
        <Button asChild variant="outline" className="h-12 px-6 border-2 border-[#1a2744] text-[#1a2744] font-bold btn-press w-full sm:w-auto justify-center" data-testid="home-paper-ballots-series-button">
          <Link to="/paper-ballots#series">VIEW THE 18-PART SERIES</Link>
        </Button>
      </div>
    </div>
  </section>
);

// 4) Lower Taxes — three points, one button. Money tools live behind More...
const LOWER_TAXES_POINTS = [
  { icon: Search, text: 'Find waste and duplication' },
  { icon: Landmark, text: 'Review management and contracts' },
  { icon: ShieldCheck, text: 'Protect essential services' },
];

const LowerTaxesHomeSection = () => (
  <section className="bg-[#f3f7ff] border-b border-slate-200 py-12 md:py-16" data-testid="home-lower-taxes-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-3xl sm:text-4xl font-black text-[#1a2744] tracking-tight leading-tight max-w-3xl" data-testid="home-lower-taxes-headline">CUT THE COST OF GOVERNMENT BEFORE ASKING TAXPAYERS FOR MORE.</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 max-w-4xl">
        {LOWER_TAXES_POINTS.map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4" data-testid="home-lower-taxes-point">
            <div className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-white" />
            </div>
            <p className="font-bold text-[#1a2744] text-sm leading-snug">{text}</p>
          </div>
        ))}
      </div>
      <Button asChild className="mt-7 h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="home-follow-money-button">
        <Link to="/lower-taxes">FOLLOW THE MONEY <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
      </Button>
      <div className="mt-5">
        <MoreDetails testId="home-lower-taxes-more">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/savings-tracker" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-savings-tracker-link"><PiggyBank className="h-4 w-4" /> Savings Tracker</Link>
            <Link to="/city-data" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-city-data-link">City Data Dashboard</Link>
            <Link to="/sunshine-list" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-sunshine-list-link">Sunshine List</Link>
            <Link to="/city-finances" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-city-finances-link">City Finances</Link>
          </div>
        </MoreDetails>
      </div>
    </div>
  </section>
);

// 5) Ward 8 — short local message, live issue snapshot, one or two highlights.
const Ward8HomeSection = ({ issues = [], news = [], meetings = [] }) => (
  <section className="bg-white border-b border-slate-200 py-12 md:py-16" data-testid="home-ward8-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black text-[#1a2744] tracking-tight leading-tight" data-testid="home-ward8-headline">WARD 8</h2>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-xl" data-testid="home-ward8-message">Local issues reported, mapped, and tracked in public, so Ward 8 gets real answers.</p>
          <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
            <Button asChild className="h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press w-full sm:w-auto justify-center" data-testid="home-view-ward8-button">
              <Link to="/ward-8">VIEW WARD 8 <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild className="h-12 px-6 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-black tracking-wide btn-press w-full sm:w-auto justify-center" data-testid="home-ward8-report-button">
              <Link to="/issue-tracker/report"><FileWarning className="h-4 w-4 mr-2" /> REPORT AN ISSUE</Link>
            </Button>
          </div>
          {/* Local highlights: latest updates + next council meeting */}
          <div className="mt-8 space-y-3" data-testid="home-ward8-highlights">
            {news.slice(0, 2).map((post) => (
              <Link key={post.id} to={`/newsroom/${post.slug}`} className="flex gap-4 group" data-testid="home-news-card">
                {post.image_url && <img src={fileUrl(post.image_url)} alt="" className="h-16 w-24 object-cover rounded-lg shrink-0" />}
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#C9A84C]">{post.category?.replace('_', ' ')}</p>
                  <p className="font-serif font-bold text-[#1a2744] leading-snug group-hover:underline decoration-[#C9A84C] decoration-2 underline-offset-4 line-clamp-2">{post.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{formatDate(post.published_at)}</p>
                </div>
              </Link>
            ))}
            {meetings[0] && (
              <Link to={`/meetings/${meetings[0].id}`} className="block bg-[#F3F5F8] rounded-xl p-4 border border-slate-200 hover:border-[#C9A84C]" data-testid="home-meeting-card">
                <div className="flex items-center gap-2 text-xs font-bold text-[#C9A84C] uppercase tracking-wider"><CalendarDays className="h-3.5 w-3.5" /> Next Council Meeting</div>
                <p className="font-serif font-bold text-[#1a2744] mt-1">{meetings[0].title}</p>
                <p className="text-sm text-slate-600 mt-0.5">{formatDate(meetings[0].date)} · {meetings[0].time}</p>
              </Link>
            )}
          </div>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/whats-happening" className="text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-whats-happening-link">See everything happening</Link>
            <Link to="/meetings" className="text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-meetings-link">All Council Meetings</Link>
            <Link to="/map" className="inline-flex items-center gap-1 text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-issue-map-link"><MapPin className="h-3.5 w-3.5" /> Public Issue Map</Link>
          </div>
        </div>
        {/* Issue snapshot: recent public PITA reports */}
        <div className="bg-[#1a2744] rounded-2xl p-6 sm:p-8" data-testid="home-ward8-issue-snapshot">
          <p className="font-bold text-white text-sm uppercase tracking-wider mb-4">Recent Public Reports</p>
          {issues.length === 0 && <p className="text-slate-400 text-sm">No public reports yet. Be the first to report a Ward 8 issue.</p>}
          <div className="space-y-3">
            {issues.slice(0, 3).map((issue) => (
              <Link key={issue.id} to={`/issue-tracker?track=${issue.tracking_number}`} className="block bg-white/10 rounded-xl p-4 hover:bg-white/15" data-testid="home-recent-issue-card">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-[#C9A84C] font-bold">{issue.tracking_number}</span>
                  <StatusBadge status={issue.status} />
                </div>
                <p className="text-white font-semibold text-sm mt-1.5 line-clamp-1">{issue.title}</p>
                <p className="text-slate-400 text-xs mt-0.5">{ISSUE_CATEGORIES[issue.category] || issue.category}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// 6) Election Centre — Election Event List button (all CKL candidate events),
// three quick tools, one button, official source note.
const ElectionHomeSection = ({ electionBtn }) => {
  const [nextEvent, setNextEvent] = useState(null);
  useEffect(() => {
    api.get('/election/events/next').then((r) => setNextEvent(r.data || null)).catch(() => {});
  }, []);
  const item = (id, fallbackLabel, fallbackUrl) => {
    const o = electionBtn ? electionBtn(id) : null;
    if (o && o.show === false) return null;
    return { label: o?.label || fallbackLabel, url: o?.url || fallbackUrl };
  };
  const tools = [
    { id: 'find-your-ward', icon: Search, ...item('find-your-ward', 'Find Your Ward', '/whos-running#find-your-ward') },
    { id: 'whos-running', icon: Users, ...item('whos-running', "Who's Running", '/whos-running') },
    { id: 'key-dates', icon: CalendarDays, label: 'Key Dates', url: '/elections/key-dates' },
  ].filter((t) => t && t.label && t.url);
  const centre = item('election-centre', 'ENTER ELECTION CENTRE', '/elections');
  const nx = nextEvent?.next;
  return (
    <section className="bg-[#f3f7ff] border-b border-slate-200 py-12 md:py-16" data-testid="home-election-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl font-black text-[#1a2744] tracking-tight leading-tight" data-testid="home-election-headline">KNOW YOUR ELECTION.</h2>
        <div className="mt-6 rounded-2xl border-2 border-[#C9A84C]/70 bg-[#FBF7EE] p-5 sm:p-6 max-w-4xl flex flex-col sm:flex-row sm:items-center gap-4" data-testid="home-election-event-list">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black uppercase tracking-widest text-[#8a6d2c] inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Every candidate event in Kawartha Lakes</p>
            <p className="mt-1 text-[#1a2744] font-bold leading-snug">Meet and greets, debates, forums, coffee with a candidate. Mayor, all 8 wards and school trustees.</p>
            {nx ? (
              <p className="mt-1.5 text-sm text-slate-700 truncate" data-testid="home-next-event">Next: <Link to={`/elections/events#event-${nx.id}`} className="font-semibold underline underline-offset-2 text-[#1a2744]">{nx.title}</Link>, {nx.date_label}{nx.start_time ? `, ${nx.time_label} ET` : ''}</p>
            ) : (
              <p className="mt-1.5 text-sm text-slate-600" data-testid="home-next-event-empty">Know of an event? Submit it and it will be reviewed before it appears.</p>
            )}
          </div>
          <Button asChild className="h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-black tracking-wide btn-press shrink-0" data-testid="home-election-event-list-button">
            <Link to="/elections/events"><ListChecks className="h-5 w-5 mr-2" /> Election Event List</Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 max-w-4xl">
          {tools.map(({ id, icon: Icon, label, url }) => (
            <Link key={id} to={url} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C9A84C] card-lift" data-testid={`home-election-tool-${id}`}>
              <div className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <p className="font-bold text-[#1a2744] text-sm leading-snug">{label}</p>
            </Link>
          ))}
        </div>
        {centre && (
          <Button asChild className="mt-7 h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="home-election-centre-button">
            <Link to={centre.url}><Landmark className="h-4 w-4 mr-2" /> {centre.label}</Link>
          </Button>
        )}
        <p className="text-[11px] text-slate-500 mt-4" data-testid="home-election-source-note">
          Source-based · candidates are unofficial until certified by the City Clerk ·{' '}
          <a href="https://www.kawarthalakes.ca/government-administration/elections/candidates-list/" target="_blank" rel="noreferrer" className="underline">Official CKL Candidates List</a>
        </p>
      </div>
    </section>
  );
};

// 7) Get Involved — three simple ways in, one primary action.
// Subscribe, contact, and campaign downloads live behind More...
const GET_INVOLVED_WAYS = [
  { id: 'volunteer', icon: HandHeart, label: 'Volunteer', url: '/volunteer' },
  { id: 'sign', icon: Vote, label: 'Request a Sign', url: '/volunteer' },
  { id: 'follow', icon: Newspaper, label: 'Follow Steve', url: '/get-involved' },
];

const GetInvolvedHomeSection = ({ actionCard }) => (
  <section className="bg-white py-12 md:py-16" data-testid="home-get-involved-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-3xl sm:text-4xl font-black text-[#1a2744] tracking-tight leading-tight" data-testid="home-get-involved-headline">CHANGE TAKES PEOPLE.</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 max-w-4xl">
        {GET_INVOLVED_WAYS.map(({ id, icon: Icon, label, url }) => (
          <Link key={id} to={url} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C9A84C] card-lift" data-testid={`home-get-involved-${id}`}>
            <div className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-white" />
            </div>
            <p className="font-bold text-[#1a2744] text-sm leading-snug">{label}</p>
          </Link>
        ))}
      </div>
      <Button asChild className="mt-7 h-12 px-6 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-black tracking-wide btn-press" data-testid="home-get-involved-button">
        <Link to="/get-involved"><HandHeart className="h-4 w-4 mr-2" /> GET INVOLVED</Link>
      </Button>
      <div className="mt-6">
        <MoreDetails testId="home-get-involved-more">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {(!actionCard('volunteer') || actionCard('volunteer').show !== false) && (
              <Card className="bg-[#1a2744] border-0 card-lift">
                <CardContent className="p-6">
                  <HandHeart className="h-8 w-8 text-[#C9A84C] mb-3" />
                  <p className="font-serif font-black text-white text-xl">{actionCard('volunteer')?.title || 'Join the team'}</p>
                  <p className="text-slate-300 text-sm mt-2 leading-relaxed">{actionCard('volunteer')?.body || 'Volunteers are the heart of this campaign. Signs, doors, calls, events. Every bit helps.'}</p>
                  <Button asChild className="mt-4 h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold w-full btn-press" data-testid="home-volunteer-button"><Link to={actionCard('volunteer')?.button_url || '/volunteer'}>{actionCard('volunteer')?.button_label || 'Volunteer'}</Link></Button>
                </CardContent>
              </Card>
              )}
              {(!actionCard('subscribe') || actionCard('subscribe').show !== false) && (
              <Card className="card-lift">
                <CardContent className="p-6">
                  <Newspaper className="h-8 w-8 text-[#1a2744] mb-3" />
                  <p className="font-serif font-black text-[#1a2744] text-xl">{actionCard('subscribe')?.title || 'Subscribe for alerts'}</p>
                  <p className="text-slate-600 text-sm mt-2 mb-4 leading-relaxed">{actionCard('subscribe')?.body || 'Campaign updates, meeting reminders, and public alerts for Ward 8.'}</p>
                  <SubscribeForm compact testId="home-subscribe-form" />
                </CardContent>
              </Card>
              )}
              {(!actionCard('contact') || actionCard('contact').show !== false) && (
              <Card className="card-lift">
                <CardContent className="p-6">
                  <Mail className="h-8 w-8 text-[#1a2744] mb-3" />
                  <p className="font-serif font-black text-[#1a2744] text-xl">{actionCard('contact')?.title || 'Contact Steve'}</p>
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed">{actionCard('contact')?.body || 'Questions, concerns, or ideas for Ward 8? Steve wants to hear from you directly.'}</p>
                  <Button asChild variant="outline" className="mt-4 h-11 border-[#1a2744] text-[#1a2744] font-bold w-full btn-press" data-testid="home-contact-button"><Link to={actionCard('contact')?.button_url || '/contact'}>{actionCard('contact')?.button_label || 'Get in Touch'}</Link></Button>
                </CardContent>
              </Card>
              )}
            </div>
            <div className="space-y-4" data-testid="home-flyer-section">
              <FlyerDownloadSection loc="home" />
              <CvDownloadSection loc="home" />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/gallery" className="inline-flex items-center text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-gallery-link"><Camera className="h-4 w-4 mr-1" /> View gallery</Link>
              <Link to="/videos" className="inline-flex items-center text-sm font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c]" data-testid="home-videos-link"><PlayCircle className="h-4 w-4 mr-1" /> All videos</Link>
            </div>
          </div>
        </MoreDetails>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [data, setData] = useState({ hero: [], home: null, meetSteve: null, platform: null, news: [], meetings: [], issues: [], settings: {} });
  const [introOpen, setIntroOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [hero, home, meetSteve, platform, news, meetings, issues, settings] = await Promise.allSettled([
        api.get('/hero'), api.get('/pages/home'), api.get('/pages/meet-steve'), api.get('/pages/platform'),
        api.get('/news?limit=3'), api.get('/meetings?when=upcoming&limit=2'),
        api.get('/issues?limit=3'), api.get('/settings'),
      ]);
      setData({
        hero: hero.value?.data || [],
        home: home.value?.data || null,
        meetSteve: meetSteve.value?.data || null,
        platform: platform.value?.data || null,
        news: news.value?.data || [],
        meetings: meetings.value?.data || [],
        issues: issues.value?.data || [],
        settings: settings.value?.data || {},
      });
    };
    load();
  }, []);

  const videoSection = data.home?.sections?.find((s) => s.type === 'video_intro');

  // ----- Steve intro video config (admin-editable in Settings -> Intro Video) -----
  const introCfg = data.settings.intro_video || {};
  const hopeTheme = data.settings.hope_theme || {};
  const introVideoUrl = introCfg.video_url || videoSection?.embed_url || '';
  const introEmbedSrc = toYouTubeEmbed(introVideoUrl, { privacy: introCfg.privacy_mode !== 'off' });
  const popupEnabled = introCfg.popup_enabled !== 'off';

  // First-visit popup: show once per browser (per popup_version key).
  // Mobile (<768px): never auto-open. The overlay was blocking first-time
  // mobile visitors from reading the hero and using the menu; the video stays
  // one click away in the Accountability section's More... area.
  useEffect(() => {
    if (!data.home) return; // wait for data load
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (popupEnabled && !isMobile && introEmbedSrc && !hasSeenIntro(introCfg)) {
      const t = setTimeout(() => setIntroOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [data.home]); // eslint-disable-line react-hooks/exhaustive-deps

  const whySection = data.home?.sections?.find((s) => s.type === 'why_vote');
  const whyCards = data.meetSteve?.why_vote_cards || [];
  const pillars = data.platform?.pillars || [];
  // Admin -> Page Content: homepage quick-link overrides (null = built-in wording)
  const pageContent = usePageContent();
  const homeContent = pageContent.home;
  const electionBtn = (id) => (homeContent?.election_buttons || []).find((b) => b.id === id) || null;
  const actionCard = (id) => (homeContent?.action_cards || []).find((c) => c.id === id) || null;

  return (
    <div>
      <HeroCarousel
        slides={data.hero}
        hope={hopeTheme}
        extraCta={introCfg.show_in_hero === 'on' && introEmbedSrc ? { label: introCfg.button_text || INTRO_DEFAULTS.button_text, onClick: () => setIntroOpen(true) } : null}
      />

      {/* Small hope tagline strip under the hero (admin-controlled; hidden when unset) */}
      <HopeTaglineStrip block={hopeTheme.hero_tagline} />

      {/* Site structure v3: concise links to every main section */}
      <HomeSectionLinks />

      <IntroVideoModal open={introOpen} onOpenChange={setIntroOpen} cfg={introCfg} videoUrl={introVideoUrl} />

      {/* 1. ACCOUNTABILITY YOU CAN SEE — six cards; the deeper story lives behind More... */}
      <AccountabilityPillarsSection
        whySection={whySection}
        whyCards={whyCards}
        pillars={pillars}
        watchVideo={introEmbedSrc ? { label: introCfg.button_text || INTRO_DEFAULTS.button_text, onClick: () => setIntroOpen(true) } : null}
      />

      {/* 2. PITA */}
      <PitaHomeSection />

      {/* 3. PAPER BALLOTS */}
      <PaperBallotsHomeSection />

      {/* 4. LOWER TAXES / ACCOUNTABILITY */}
      <LowerTaxesHomeSection />

      {/* 5. WARD 8 */}
      {/* Beautiful Ward 8 photo strip (hidden until the album has photos) */}
      <Ward8PhotoStrip />
      <Ward8HomeSection issues={data.issues} news={data.news} meetings={data.meetings} />

      {/* REVIEWS AND TESTIMONIALS (added at Steve's request, Phase 109) */}
      <ReviewsHomeSection />

      {/* 6. ELECTION CENTRE */}
      <ElectionHomeSection electionBtn={electionBtn} />

      {/* Lesperance is the French word for Hope (admin-controlled; hidden when unset) */}
      <HopeHomepageSection hope={hopeTheme} />

      {/* 7. GET INVOLVED */}
      <GetInvolvedHomeSection actionCard={actionCard} />
    </div>
  );
}
